import type { SubjectTrack } from '@captionflow/caption-engine';
/**
 * Thin typed API client. Uses cookie-based sessions (credentials: include).
 * Every call returns parsed JSON or throws an ApiClientError with the server's
 * error code so the UI can react (e.g. re-auth on 401).
 */
import type {
  ProjectSummaryDTO,
  EnhanceResponseDTO,
  TranslateResponseDTO,
  MagicState,
  EntitlementsDTO,
  Plan,
  PlanId,
  BillingInterval,
  Currency,
} from '@captionflow/shared';

/** Raw resource usage (not billed): kMagic runs, render WALL-time minutes
 *  (compute, not output length), and Modal depth mattes. */
export interface AdminUsage {
  kmagicRuns: number;
  kmagicCostUsd: number; // real OpenRouter spend for kMagic
  renderMinutes: number;
  depthMattes: number;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: 'user' | 'admin';
  emailVerified: boolean;
  country: string | null;
  createdAt: string;
  projectCount: number;
  plan: string | null;
  subscriptionStatus: string | null;
  usage: AdminUsage;
}

export interface AdminUserProject {
  id: string;
  name: string;
  status: string;
  aspectRatio: string;
  renderCount: number;
  createdAt: string;
  updatedAt: string;
  usage: AdminUsage;
}

export interface AdminUserProjectsResponse {
  projects: AdminUserProject[];
  total: AdminUsage;
  byMonth: (AdminUsage & { month: string })[];
}

export interface StockItem {
  id: string;
  source: 'pexels' | 'pixabay' | 'openverse';
  kind: 'image' | 'video';
  thumbUrl: string;
  downloadUrl: string;
  width: number;
  height: number;
  durationSec?: number;
  author?: string;
}

export interface StockAudioItem {
  id: string;
  source: string;
  title: string;
  artist: string;
  audioUrl: string;
  durationSec: number;
}

export interface BillingMe {
  entitlements: EntitlementsDTO;
  billingConfigured: boolean;
  country: string | null;
  gstin: string | null;
  subscription: {
    plan: PlanId;
    interval: string;
    status: string;
    currency: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  invoices: {
    id: string;
    amountCents: number;
    currency: string;
    status: string;
    invoiceUrl: string | null;
    createdAt: string;
  }[];
}

const BASE = '/api/v1';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when we actually send a body - Fastify
  // rejects an empty body when content-type is application/json (bodyless POSTs
  // like retranscribe/logout would otherwise 400/500).
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
  if (init?.body != null && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = body?.error ?? { code: 'unknown', message: res.statusText };
    throw new ApiClientError(res.status, err.code, err.message);
  }
  return body as T;
}

export const api = {
  register: (email: string, password: string) =>
    req<{ id: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    req<{ id: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => req<void>('/auth/logout', { method: 'POST' }),
  me: () => req<{ id: string; email: string; displayName: string | null; role: 'user' | 'admin' }>('/auth/me'),

  // Admin panel (server-gated by role === 'admin'; 403 otherwise).
  adminListUsers: () =>
    req<{ users: AdminUser[] }>('/admin/users'),
  adminUserProjects: (userId: string) =>
    req<AdminUserProjectsResponse>(`/admin/users/${userId}/projects`),

  listProjects: () => req<{ projects: ProjectSummaryDTO[] }>('/projects'),
  createProject: (name: string, aspectRatio = '9:16', language?: string) =>
    req<ProjectSummaryDTO>('/projects', {
      method: 'POST',
      body: JSON.stringify({ name, aspectRatio, ...(language ? { language } : {}) }),
    }),
  getProject: (id: string) => req<ProjectSummaryDTO>(`/projects/${id}`),
  renameProject: (id: string, name: string) =>
    req<ProjectSummaryDTO>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteProject: (id: string) => req<void>(`/projects/${id}`, { method: 'DELETE' }),

  getPipeline: (id: string) =>
    req<{
      name: string;
      status: string;
      overall: 'processing' | 'ready' | 'failed';
      pipeline: Record<string, { status: string; progress?: number; detail?: string }>;
    }>(`/projects/${id}/pipeline`),

  getTranscript: (id: string) =>
    req<{
      provider: string | null;
      language: string | null;
      status: string | null;
      durationMs: number | null;
      aspectRatio?: '9:16' | '16:9' | '1:1' | '4:5' | 'source' | null;
      sourceWidth?: number | null;
      sourceHeight?: number | null;
      sourceUrl: string | null;
      proxyUrl?: string | null;
      templateId?: string | null;
      words: {
        id: string;
        text: string;
        startMs: number;
        endMs: number;
        confidence?: number;
        emphasized: boolean;
        hidden: boolean;
        isFiller: boolean;
        colorOverride?: string;
        sizeScale?: number;
        bold?: boolean;
        depthBehind?: boolean;
      }[];
      segments: {
        id: string;
        wordIds: string[];
        startMs: number;
        endMs: number;
        templateId?: string | null;
      }[];
      styleOverrides?: Record<string, unknown>;
      placementOverride?: Record<string, unknown>;
      transitions?: { id: string; kind: 'fade' | 'flash' | 'zoomIn' | 'zoomOut' | 'slide'; startMs: number; durationMs: number }[];
      brolls?: {
        id: string; kind: 'image' | 'video'; name: string; url: string; storageKey: string;
        startMs: number; durationMs: number; mode: 'cover' | 'overlay'; x: number; y: number; scale: number; fit: 'cover' | 'contain';
      }[];
      /** As-created snapshot for "Reset to original" (null on legacy projects). */
      initial?: {
        templateId?: string;
        emphasizedWordIds?: string[];
        segments?: { idx: number; wordIds: string[]; startMs: number; endMs: number }[];
      } | null;
    }>(`/projects/${id}/transcript`),

  startMagic: (projectId: string, instruction?: string) =>
    req<{ ok: boolean }>(`/projects/${projectId}/magic`, {
      method: 'POST',
      body: JSON.stringify({ instruction: instruction || undefined }),
    }),
  getMagic: (projectId: string, run?: string) =>
    req<
      MagicState & {
        frameUrls?: string[];
        runs?: { id?: string; status: string; stage: string; startedAt?: string; finishedAt?: string; instruction?: string }[];
      }
    >(`/projects/${projectId}/magic${run ? `?run=${encodeURIComponent(run)}` : ''}`),
  /** Mark a kMagic run's result as applied (so the tab stops prompting after a refresh). */
  markMagicApplied: (projectId: string, runId?: string) =>
    req<{ ok: boolean }>(`/projects/${projectId}/magic/applied`, {
      method: 'POST',
      body: JSON.stringify({ runId: runId || undefined }),
    }),

  startCutout: (projectId: string) =>
    req<{ ok: boolean; status: 'running' }>(`/projects/${projectId}/cutout`, { method: 'POST' }),
  getCutout: (projectId: string) =>
    req<{
      status: 'idle' | 'running' | 'ready' | 'failed';
      progress: number;
      width: number | null;
      height: number | null;
      error: string | null;
      track: SubjectTrack | null;
      url: string | null;
    }>(`/projects/${projectId}/cutout`),
  /** Derive the head track (for hooks that follow the subject) from the cut-out. */
  startTrack: (projectId: string) =>
    req<{ ok: boolean }>(`/projects/${projectId}/track`, { method: 'POST' }),

  // --- Studio-sound audio enhancer (denoise + EQ + loudness) ---
  startAudioEnhance: (projectId: string, params: { strength: number; loudnorm: number }) =>
    req<{ ok: boolean; status: 'running' }>(`/projects/${projectId}/audio-enhance`, {
      method: 'POST',
      body: JSON.stringify(params),
    }),
  setAudioEnhanceEnabled: (projectId: string, enabled: boolean) =>
    req<{ ok: boolean; enabled: boolean }>(`/projects/${projectId}/audio-enhance/enabled`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
  getAudioEnhance: (projectId: string) =>
    req<{
      status: 'idle' | 'running' | 'ready' | 'failed';
      progress: number;
      enabled: boolean;
      strength: number;
      loudnorm: number;
      error: string | null;
      stale: boolean;
      url: string | null;
    }>(`/projects/${projectId}/audio-enhance`),

  // --- Stock B-roll search (Pexels / Pixabay / Openverse) ---
  stockSearch: (projectId: string, q: string, type: 'all' | 'image' | 'video' = 'all') =>
    req<{ items: StockItem[]; enabled: boolean }>(
      `/projects/${projectId}/stock/search?q=${encodeURIComponent(q)}&type=${type}`,
    ),
  stockAdd: (
    projectId: string,
    item: { source: string; kind: 'image' | 'video'; downloadUrl: string; name?: string; durationSec?: number },
  ) =>
    req<{ storageKey: string; url: string; kind: 'image' | 'video'; name: string; durationMs?: number }>(
      `/projects/${projectId}/stock/add`,
      { method: 'POST', body: JSON.stringify(item) },
    ),

  musicSearch: (projectId: string, q: string) =>
    req<{ items: StockAudioItem[]; enabled: boolean }>(
      `/projects/${projectId}/stock/music/search?q=${encodeURIComponent(q)}`,
    ),
  musicAdd: (
    projectId: string,
    item: { source: string; audioUrl: string; title?: string; artist?: string; durationSec?: number },
  ) =>
    req<{ storageKey: string; url: string; name: string; durationMs?: number }>(
      `/projects/${projectId}/stock/music/add`,
      { method: 'POST', body: JSON.stringify(item) },
    ),

  brollUpload: (projectId: string, filename: string, contentType: string, sizeBytes: number) =>
    req<{ storageKey: string; url: string }>(`/projects/${projectId}/broll-uploads`, {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, sizeBytes }),
    }),

  requestUpload: (projectId: string, filename: string, contentType: string, sizeBytes: number, durationMs?: number) =>
    req<{ uploadId: string; url: string; fields?: Record<string, string> }>(
      `/projects/${projectId}/uploads`,
      { method: 'POST', body: JSON.stringify({ filename, contentType, sizeBytes, durationMs }) },
    ),
  confirmUpload: (projectId: string, uploadId: string) =>
    req<{ mediaAssetId: string }>(`/projects/${projectId}/uploads/confirm`, {
      method: 'POST',
      body: JSON.stringify({ uploadId }),
    }),

  saveCaptions: (projectId: string, payload: unknown) =>
    req<{ ok: boolean; revision: number }>(`/projects/${projectId}/captions`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  retranscribe: (projectId: string, language?: string) =>
    req<{ ok: boolean }>(`/projects/${projectId}/retranscribe`, {
      method: 'POST',
      body: JSON.stringify(language ? { language } : {}),
    }),

  enhance: (
    projectId: string,
    words: { id: string; text: string; startMs: number; endMs: number }[],
    language?: string | null,
  ) =>
    req<EnhanceResponseDTO>(`/projects/${projectId}/enhance`, {
      method: 'POST',
      body: JSON.stringify({ words, language: language ?? undefined }),
    }),

  translate: (
    projectId: string,
    words: { id: string; text: string; startMs: number; endMs: number }[],
    targetLanguage: string,
    sourceLanguage?: string | null,
  ) =>
    req<TranslateResponseDTO>(`/projects/${projectId}/translate`, {
      method: 'POST',
      body: JSON.stringify({ words, targetLanguage, sourceLanguage: sourceLanguage ?? undefined }),
    }),

  export: (projectId: string, settings: unknown) =>
    req<{ renderJobId: string; cached: boolean }>(`/projects/${projectId}/exports`, {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  getExport: (projectId: string, jobId: string) =>
    req<{ id: string; status: string; stage: string; progress: number; error: string | null }>(
      `/projects/${projectId}/exports/${jobId}`,
    ),
  getExportDownload: (projectId: string, jobId: string) =>
    req<{ url: string; width: number; height: number; durationMs: number }>(
      `/projects/${projectId}/exports/${jobId}/download`,
    ),
  getLatestExport: (projectId: string) =>
    req<{ job: { id: string; status: string; stage: string; progress: number; error: string | null; startedAt: string } | null }>(
      `/projects/${projectId}/exports/latest`,
    ),
  getLatestRender: (projectId: string) =>
    req<{ url: string | null; jobId?: string; finishedAt?: string | null }>(
      `/projects/${projectId}/renders/latest`,
    ),
  // --- Billing ---
  getBilling: () => req<BillingMe>('/billing/me'),
  getPlans: () =>
    req<{ plans: Record<PlanId, Plan>; paidPlanIds: PlanId[]; currency: Currency; configured: boolean }>(
      '/billing/plans',
    ),
  checkout: (planId: Exclude<PlanId, 'free'>, interval: BillingInterval, opts?: { country?: string; gstin?: string }) =>
    req<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ planId, interval, country: opts?.country, gstin: opts?.gstin }),
    }),
  changePlan: (planId: Exclude<PlanId, 'free'>, interval: BillingInterval) =>
    req<{ ok: boolean }>('/billing/change-plan', { method: 'POST', body: JSON.stringify({ planId, interval }) }),
  cancelSubscription: () => req<{ ok: boolean }>('/billing/cancel', { method: 'POST' }),
  resumeSubscription: () => req<{ ok: boolean }>('/billing/resume', { method: 'POST' }),

  getRecentRenders: (projectId: string) =>
    req<{
      jobs: {
        id: string;
        status: string;
        stage: string;
        progress: number;
        error: string | null;
        attempts: number;
        workerId: string | null;
        resolution: string | null;
        fps: number | null;
        aspectRatio: string | null;
        createdAt: string;
        startedAt: string | null;
        finishedAt: string | null;
        elapsedMs: number | null;
        output: { width: number; height: number; durationMs: number } | null;
      }[];
    }>(`/projects/${projectId}/renders/recent`),
};
